/* eslint-disable complexity */
import React from "react";
import PropTypes from "prop-types";
import agentSpec from '@lmig/dotcom-aspect-helpers/specs/agent.spec';
import Image from '@lmig/dotcom-aspect-components/Utility/Image';
import PhoneNumber from '@lmig/dotcom-aspect-components/Utility/PhoneNumber';
import { Link, IconSmartphone, IconEmail, IconLocations, Button } from '@lmig/lmds-react';
import css from 'styled-jsx/css';
import Rating from '../Rating';

const { className: learnMoreClass, styles: learnMoreStyles } = css.resolve`
  .lm-Link{
    font-weight: bold;
  }
`;

const { className: quoteButtonClass, styles: quoteButtonStyles } = css.resolve`
  .lmig-Button{
    margin: 1rem 0 2rem 0;
  }
`;

const AgentListCard = ({
  agent,
  className = '',
  showReviews = true,
  linkedName = true,
  isAssociate = false,
  showContactInfo = true,
  quoteButton = false,
  pin,
  showCityState = false,
}) => {
  const { displayName, email, image, phones, aliasPath, rating, reviewCount, firstName, title, preferredName, alias } = agent;
  let imgSrc = 'comparion/entity/agent/blank-profile-photo.jpg';
  let imgAlt = 'Placeholder Image, Insurance Agent | Comparion';
  let numReviews = 0;
  let agentName = preferredName || firstName || displayName;
  const office = agent?.offices?.find(({ officeType }) => officeType === "FIELD");
  const remoteAgentWithLocalOffice = agent?.isRemote && agent?.localOffice;
  const isRemoteOfficeValid = remoteAgentWithLocalOffice && agent?.localOffice?.city && agent?.localOffice?.state?.name && agent?.localOffice?.state?.code;
  const officeAddress = isRemoteOfficeValid ? agent?.localOffice : office?.address;

  if (/\s/g.test(agentName)) {
    agentName = displayName;
  }

  if (image) {
    imgSrc = image.src || imgSrc;
    imgAlt = image.alt || imgAlt;
  }

  if (reviewCount) {
    numReviews = reviewCount;
  }

  // eslint-disable-next-line no-nested-ternary
  const agentPhone = phones?.primary ? phones.primary : (phones?.cell ? phones.cell : null);
  const agentTitle = title ? `Comparion ${title}` : "Comparion Insurance Agent";
  const imageDimensions = isAssociate ? "6.5rem" : "7.5rem";

  return (
    <div className={className}>
      <div id="floatingCard">
        <div className={`agentContainer ${isAssociate ? "isAssociate" : ''}`} data-key={displayName}>
          {pin && <div className="pin">
            <IconLocations label={pin.distanceAway && `${pin.distanceAway.toFixed(2)} mi`} className="pinMarker" />
            <span className="index">{String.fromCharCode(pin.index + 65)}</span>
          </div>}
          <div className="agentImage">
            <Image type="circle" imageDomain="IMAGE_EMS" deferLoad={false}>
              <img src={imgSrc} alt={imgAlt} width={imageDimensions} height={imageDimensions} />
            </Image>
          </div>
          <div className="agentCard">
            <div className="nameWrapper">
              <div className="nameHeader">
                {linkedName
                  ? <Link variant="standalone" caretPosition="none" className={learnMoreClass} href={`/${aliasPath}`}>{displayName}</Link>
                  : <span>{displayName}</span>
                }
              </div>
              <div className="nameSubHeader">{agentTitle}</div>
            </div>
            {showCityState && <div className="addressSection">
              {officeAddress?.city}, {officeAddress?.state?.code}
            </div>}
            {showContactInfo && <div className={`${!agentPhone ? "noPhoneContactInfo" : "contactInfo"}`}>
              {!!agentPhone &&
                <div className="contactPhone">
                  <span>
                    <IconSmartphone size="24" />
                  </span>
                  <PhoneNumber tel={agentPhone.number}>
                    <Link
                      variant="standalone"
                      caretPosition="none"
                      aria-label="Contact us"
                    />
                  </PhoneNumber>
                </div>
              }
              <div className="email">
                <IconEmail icon="Email" size="24" />
                <Link variant="standalone" caretPosition="none" href={`mailto:${email}`}>
                  Email {agentName}
                </Link>
              </div>
            </div>}
            {showReviews && <Rating rating={rating} count={numReviews} />}
            {quoteButton && <Button size="small" href={`/quote?agent=${alias}`} rel="nofollow" className={quoteButtonClass} data-testid={`quote-agent-${alias}`}>Quote with {displayName}</Button>}
          </div>
        </div>
        {learnMoreStyles}
        {quoteButtonStyles}
      </div>
      <style global jsx>{`@import './AgentListCard.scoped.scss';`}</style>
    </div>
  );
};

AgentListCard.propTypes = {
  agent: PropTypes.shape(agentSpec).isRequired,
  showReviews: PropTypes.bool,
  linkedName: PropTypes.bool,
  isAssociate: PropTypes.bool,
  showContactInfo: PropTypes.bool,
  quoteButton: PropTypes.bool,
  className: PropTypes.string,
  pin: PropTypes.object,
};

export default AgentListCard;